define("tmds/Schematic", [
	"jquery",
	"d3",
	"tmds/config",
	"tmds/geometry/Point",
	"tmds/geometry/Polyline",
	"tmds/layers/Layer",
	"tmds/utils/tmdsUtils",
	'tmds/validator'
], function($, d3, config, Point, Polyline, Layer, tmdsUtils, validator) {
	"use strict";
	
	/**
	 * TMDS layer names
	 */
	var constants = {
		TMDS_TRACK: "tracks",
		TMDS_SIGNAL: "signals",
		TMDS_SWITCH: "switches",
		TMDS_INTERLOCKER: "interlockers",
		TMDS_ALPHATEXT: "alphatexts",
		TMDS_CONTROLPOINT: "controlpoints"
	};
	
	/**
	 * mapping fields for each track
	 */
	var trackFields = {
		TRACK_GUID: "tmdsUid",
		TRACK_SUBDIV: "subdivisionName",
		TRACK_NAME: "trackName",					// trackName
		TRACK_ALTERNAME: "alternateTrackNames",		// alternateTrackNames
		TRACK_BMP: "beginMp",
		TRACK_EMP: "endMp",
		TRACK_MPSUF: "mpSuffix",
		TRACK_LX: "lx",
		TRACK_LY: "ly",
		TRACK_RX: "rx",
		TRACK_RY: "ry",
		TMDS_VERSION:"databaseVersion",
		PTC_VERSION:"versionPtcReference"
	};

	// deferred objects to handle both schematic and mpRange service call
	var defMaster = null,
		defTMDS = null,
		defMPRange = null,
        defMPRangeGraph = null;
	
	/**
	* function to compare two mp suffixes. considers undefined, null, empty string, white space to all be equivilant.
	* @param sufOne
	* @param sufTwo
	*/
	function matchSuffix(sufOne, sufTwo){
	   sufOne = sufOne ? sufOne.trim() : "";
	   sufTwo = sufTwo ? sufTwo.trim() : "";
	   
	   return sufOne === sufTwo;
	}

	/**
	 * reformat the milepost range list
	 * @param mpRangeInfo {Object} the milepost range info object that returned from the mprange web service
	 * It has the following data structure
	 * 		{
	 * 			defaultDirection: "E" | "S" | "W" | "N",		// default heading direction
	 * 			defaultTimezone: "P" | "M" | "C" | "E",			
	 * 			milePostRangeList: [{							// array of milepost ranges
	 * 				fromMp:	<from milepost>,
	 * 				fromMpSfx: <from milepost suffix>,
	 * 				toMp: <to milepost>,
	 * 				toMpSfx: <to milepost suffix>
	 * 			},{
	 * 				...
	 * 			}],
	 * 			subDivFullName: "SAN BERNARDINO", 				// subdivision full name
	 * 			subDivName: "SANBER",							// subdivision short name
	 * 			usesGTB: "Y" | "N"
	 * 		}
	 * @return {Object} It will keep all properties of mpRangeInfo, except milePostRangeList will become
	 * 		[{
	 * 			from: <from milepost>,
	 * 			to: <to milepost>,
	 * 			suffix: <milepost suffix>,
	 * 			sequence: <milepost range sequence>
	 * 		},{
	 * 			...
	 * 		}]
	 */
	function formatMpRangeInfo(mpRangeInfo) {
		var schMpRng = [],
			funcMapMPRangeList = function(mpRangeList, reverse) {
				var result = [];
				
				mpRangeInfo.milePostRangeList.forEach(function(range, i) {
					result.push({
						"from": reverse ? range.toMp : range.fromMp,
						"to": reverse ? range.fromMp : range.toMp,
						"suffix": range.fromMpSfx
					});
				});
				
				return result;
			};
		
		// for defaultDirection = [S | W], swtich from and to milepost, and reverse the range order
		if (['S','W'].indexOf(mpRangeInfo.defaultDirection) > -1) {
			schMpRng = funcMapMPRangeList(mpRangeInfo.milePostRangeList, true);
			mpRangeInfo.milePostRangeList = schMpRng.reverse();
		}
		else {
			schMpRng = funcMapMPRangeList(mpRangeInfo.milePostRangeList, false);
			mpRangeInfo.milePostRangeList = schMpRng;
		}
		
		// add sequence number for each range
		mpRangeInfo.milePostRangeList.forEach(function(range, i) {
			range.sequence = i;
		});

		return mpRangeInfo;
	}

	/**
	 * return the milepost range object by the given milepost and suffix
	 * @param subdiv {String} subdivision short name
	 * @param mp {Number} milepost value
	 * @param mpSuf {String} milepost suffix
	 * @return {Object} as below structure
	 * 		{
	 * 			sequence: <milepost range sequence within a sub>,
	 * 			from: <from milepost>,
	 * 			to: <to milepost>,
	 * 			suffix: <milepost suffix>
	 * 		}
	 * Returns null if the given subdiv does not match or mp + mpSuf falls out of the range
	 */
	function getRangeByMP(subdiv, mp, mpSuf) {
		var mpRange = null;
	 	
		if (this.mpRangeInfo && subdiv === this.mpRangeInfo.subDivName) {
		 	for (var i = 0; i < this.mpRangeInfo.milePostRangeList.length; i++) {
		 		mpRange = this.mpRangeInfo.milePostRangeList[i];
		 		if (matchSuffix(mpSuf, mpRange.suffix) && 
		 				((mp >= mpRange.from && mp <= mpRange.to) || 
		 				(mp <= mpRange.from && mp >= mpRange.to))) {
		 			break;
		 		}
		 	}
		}
	 	
	 	return mpRange;
	}
	
	/**
	 * return the milepost range a given track is falling inside
	 * @param track {tmds.SvgElement} a track element
	 * @return {Object} a milepost range object
	 * @see getRangeByMP() for the details of the milepost range object
	 * @note it's safe to assume that the whole track will be within the same milepost range.
	 */
	function getRangeByTrack(track) {
		var attrs = track.attributes,
			trkSub = attrs[trackFields.TRACK_SUBDIV],
			trkBMP = attrs[trackFields.TRACK_BMP],
			trkMPSuf = attrs[trackFields.TRACK_MPSUF];
		
	 	return getRangeByMP.call(this, trkSub, trkBMP, trkMPSuf);
	}

	/**
	 * validate the input scale value against a scale extent
	 * @param scale {Number} scale number
	 * @param scaleExtent {Array} [minScale, maxScale]
	 * @return {Number} | null
	 */
	function validateScale(scale, scaleExtent) {
		var newScale;

		if (typeof scale === "number") {
			newScale = scale > scaleExtent[0] ? (scale < scaleExtent[1] ? scale : scaleExtent[1]) : scaleExtent[0];
		}
		
		return newScale;
	}
	
	/**
	 * find the tracks that can host the point by input subdivision, track name and milepost.
	 * @param lyrTrack {tmds.layers.Layer} the track layer
	 * @param subdiv {String} subdivision name
	 * @param trkName {String} track name like Main 1, Main 2, Siding, etc.
	 * @param mp {Number} milepost value
	 * @param mpSuf {String} milepost suffix
	 * @return {Array} array of the tracks that match the given subdivision, track name, milepost suffix and the track milepost range cover the given milepost
	 */
	function findHostTracks(lyrTrack, subdiv, trkName, mp, mpSuf) {
		return lyrTrack.elements.filter(function(elem) {
			var attrs = elem.attributes,
				trkSub = attrs[trackFields.TRACK_SUBDIV],
				trkTrackName = attrs[trackFields.TRACK_NAME],
				trkBMP = attrs[trackFields.TRACK_BMP],
				trkEMP = attrs[trackFields.TRACK_EMP],
				trkMPSuf = attrs[trackFields.TRACK_MPSUF];
				
			return trkSub === subdiv && 
					trkTrackName === trkName && 
					matchSuffix(trkMPSuf, mpSuf) && 
					((trkBMP <= mp && trkEMP >= mp) || 
					(trkBMP >= mp && trkEMP <= mp)); 
		});
	}
	
	/**
	 * find the inner tracks that are falling (bmp, emp) with matching subdivision and trackname.
	 * @param lyrTrack {tmds.layers.Layer} the track layer
	 * @param subdiv {String} subdivision name
	 * @param trkName {String} track name like Main 1, Main 2, Siding, etc.
	 * @param bmp {Number} beginning milepost value
	 * @param emp {Number} end milepost value
	 * @param bmpSuf {String} optional. beginning milepost suffix
	 * @param empSuf {String} optional. end milepost suffix
	 * @return {Array} array of tracks that fall within (bmp, emp) with matching subdivision and trackname.
	 */
	function findInnerTracks(lyrTrack, subdiv, trkName, bmp, emp, bmpSuf, empSuf) {
		var bRange = getRangeByMP.call(this, subdiv, bmp, bmpSuf),
			eRange = getRangeByMP.call(this, subdiv, emp, empSuf);
		
		var lRange = (bRange.sequence <= eRange.sequence) ? bRange : eRange,		// milepost range object for the left point
			rRange = (bRange.sequence <= eRange.sequence) ? eRange : bRange;		// milepost range object for the right point
		
		// milepost of the left/right point
		var lmp, rmp; 
		if (bRange.sequence === eRange.sequence) {
			lmp = Math.abs(bmp - bRange.from) < Math.abs(emp - bRange.from) ? bmp : emp;
			rmp = Math.abs(bmp - bRange.from) < Math.abs(emp - bRange.from) ? emp : bmp;
		}
		else {
			lmp = (bRange.sequence < eRange.sequence) ? bmp : emp;
			rmp = (bRange.sequence < eRange.sequence) ? emp : bmp;
		}
			
		return lyrTrack.elements.filter(function(elem) {
			var attrs = elem.attributes,
				trkTrackName = attrs[trackFields.TRACK_NAME],
				trkBMP = attrs[trackFields.TRACK_BMP],
				trkEMP = attrs[trackFields.TRACK_EMP],
				trkRange = getRangeByTrack.call(this, elem);
			
			var result = false;
			
			// if the track is not in the subdivision, skip it
			if (!trkRange) {
				return result;
			}
			
			// if left milepost range = right milepost range
			if (lRange.sequence === rRange.sequence) {
				// if track milepost range = left/right milepost range, 
				// then return tracks fall inside (lmp, rmp)
				if (trkRange.sequence === lRange.sequence) {
					result = (trkBMP > lmp && trkEMP < rmp) || (trkBMP < lmp && trkEMP > rmp); 
				}
			}
			
			//
			// else left milepost range < right milepost range
			//
			
			// if track milepost range = left milepost range, 
			// then return tracks fall inside (lmp, lRange.to]
			else if (trkRange.sequence === lRange.sequence) {
				result = (trkBMP > lmp && trkEMP <= lRange.to) || (trkBMP < lmp && trkEMP >= lRange.to); 
			}
			// if track milepost range = right milepost range, 
			// then return tracks fall inside [rRange.from, rmp)
			else if (trkRange.sequence === rRange.sequence) {
				result = (trkBMP >= rRange.from && trkEMP < rmp) || (trkBMP <= rRange.from && trkEMP > rmp); 
			}
			// otherwise, return tracks whose milepost range falls inside (lRange, rRange) 
			else {
				result = trkRange.sequence > lRange.sequence && trkRange.sequence < rRange.sequence;
			}
			
			return result && trkTrackName === trkName; 
		}, this);
	}
	
	/**
	 * Get the point by milepost value on a track per interpolation
	 * @param track {tmds.EvgElement} track svg element
	 * @param mp {Number} milepost value
	 * @return {tmds.geometry.Point}
	 */
	function trackInterpolate(track, mp) {
		var trkLX = track.attributes[trackFields.TRACK_LX],
			trkLY = track.attributes[trackFields.TRACK_LY],
			trkRX = track.attributes[trackFields.TRACK_RX],
			trkRY = track.attributes[trackFields.TRACK_RY],
			trkBMP = track.attributes[trackFields.TRACK_BMP],
			trkEMP = track.attributes[trackFields.TRACK_EMP];

		var px = trkEMP === trkBMP ? trkLX : (((trkRX - trkLX) * (mp - trkBMP)) / (trkEMP - trkBMP)) + trkLX,
			py = trkEMP === trkBMP ? trkLY : (((trkRY - trkLY) * (mp - trkBMP)) / (trkEMP - trkBMP)) + trkLY;
		
		return new Point({
			x: px,
			y: py
		});
	}
	
	/**
	 * do nothing but to stop propagating a d3 event
	 * used by ZoomIn and ZoomOut button to prevent interfering with other event handler
	 */
	function doNothing() {
		d3.event.stopPropagation();
	}
	
    /**
     * switch track name with track alternate name
     * @param tracks {Array} each array item represents the track attributes
     * @note 
     *  1. This function is to solely switch the two attribute values: TRACK_NAME, TRACK_ALTERNAME,
     *      although TRACK_NAME might host the actual alternate track name.
     *  2. This function does not set the useTrackAltName property for the track layer.
     *      The code, where to invoke the function, should set it instead.
     */
    function _switchTrackName(tracks) {
        tracks.forEach(function(aTrack) {
            var trackName = aTrack[trackFields.TRACK_NAME],
                alternativeNames = aTrack[trackFields.TRACK_ALTERNAME].trim(),
                alterName = alternativeNames && alternativeNames.split("|")[0];
				
            if (alterName) {
				aTrack[trackFields.TRACK_NAME] = alterName;
				aTrack[trackFields.TRACK_ALTERNAME] = trackName;
			}
        });
    }
    
	/**
	 * load the track data to schematic
	 * @param data {Array} of track data
	 * @param options {Object} contains optional parameters as below
	 * 		useTrackAltName {Boolean} true to switch the values of trackName and alternativeName. 
	 * 		renderMode {String} defines how tracks will be rendered
	 * @note TRACK_ALTERNAME has the value format as name1|, name1|name2|name3
	 * Choose the first name value as the alternative name if useTrackAltName 
	 */
	function _loadTracks(data) {
		// if useTrackAltName, switch the values of trackName and alternativeName
		if (this.useTrackAltName) {
            _switchTrackName(data);
		}

		var renderMode = (this.renderMode) || "TMDS";
		var uvrTrack = tmdsUtils.getTrackRenderer(renderMode);
		
		var metadata = {
			geometryType: "Line",
			geometryInfo: {
				x1: "lx",		// field name for the first point that contains the x coordinate 
				y1: "ly",		// field name for the first point that contains the y coordinate
				x2: "rx",		// field name for the second point that contains the x coordinate
				y2: "ry"		// field name for the second point that contains the y coordinate
			}
		};
		
		var lyrTrack = this.getLayer(constants.TMDS_TRACK);
		
		if (lyrTrack) {
			lyrTrack.loadData(data, metadata);
		}
		else {
			lyrTrack = new Layer(data, metadata, {
				renderer: uvrTrack.toJson()
			});
			
			this.addLayer(lyrTrack, {tmdsType: constants.TMDS_TRACK});
		}
        
        lyrTrack["useTrackAltName"] = !!(this.useTrackAltName);
	}
	
	/**
	 * load the switch data to schematic
	 * @param data {Array} of switch data
	 */
	function _loadSwitches(data) {
		var renSwitch = tmdsUtils.getSwitchRenderer();
		
		var metadata = {
			geometryType: "Point",
			geometryInfo: {
				x: "lx",		// field name that contains the x coordinate of the switch 
				y: "ly"			// field name that contains the y coordinate of the switch
			}
		};
		
		var lyrSwitch = this.getLayer(constants.TMDS_SWITCH);
		
		if (lyrSwitch) {
			lyrSwitch.loadData(data, metadata);
		}
		else {
			lyrSwitch = new Layer(data, metadata, {
				renderer: renSwitch.toJson()
			});
			
			this.addLayer(lyrSwitch, {tmdsType: constants.TMDS_SWITCH});
		}
	}
	
	/**
	 * load the signal data to schematic
	 * @param data {Array} of signal data
	 */
	function _loadSignals(data) {
		var uvrSignal = tmdsUtils.getSignalRenderer();
		
		var metadata = {
			geometryType: "Point",
			geometryInfo: {
				x: "lx",		// field name that contains the x coordinate of the signal
				y: "ly"			// field name that contains the y coordinate of the signal
			}
		};
		
		var lyrSignal = this.getLayer(constants.TMDS_SIGNAL);
		
		if (lyrSignal) {
			lyrSignal.loadData(data, metadata);
		}
		else {
			lyrSignal = new Layer(data, metadata, {
				renderer: uvrSignal.toJson()
			});
		
			this.addLayer(lyrSignal, {tmdsType: constants.TMDS_SIGNAL});
		}
	}
	
	/**
	 * load the interlocker data to schematic
	 * @param data {Array} of interlocker data
	 */
	function _loadInterlockers(data) {
		var renInterlocker = tmdsUtils.getInterlockerRenderer();
		
		var metadata = {
			geometryType: "Point",
			geometryInfo: {
				x: "lx",
				y: "ly"
			}
		};
		
		var lyrInterlocker = this.getLayer(constants.TMDS_INTERLOCKER);
		
		if (lyrInterlocker) {
			lyrInterlocker.loadData(data, metadata);
		}
		else {
			lyrInterlocker = new Layer(data, metadata, {
				renderer: renInterlocker.toJson()
			});
			
			this.addLayer(lyrInterlocker, {tmdsType: constants.TMDS_INTERLOCKER});
		}
	}
	
	/**
	 * load the control point data to schematic
	 * @param data {Array} of control point data
	 */
	function _loadControlPoints(data) {
		var renCP = tmdsUtils.getControlPointRenderer();
		
		var metadata = {
			geometryType: "Point",
			geometryInfo: {
				x: "lx",
				y: "ly"
			}
		};
		
		var lyrCP = this.getLayer(constants.TMDS_CONTROLPOINT);
		
		if (lyrCP) {
			lyrCP.loadData(data, metadata);
		}
		else {
			lyrCP = new Layer(data, metadata, {
				renderer: renCP.toJson()
			});
			
			this.addLayer(lyrCP, {tmdsType: constants.TMDS_CONTROLPOINT});
		}
	}
	
	/**
	 * load the alpha text data to schematic
	 * @param data {Array} of alpha text data
	 */
	function _loadAlphaTexts(data) {
		var renAlphaText = tmdsUtils.getAlphaTextRenderer();
		
		var metadata = {
			geometryType: "Point",
			geometryInfo: {
				x: "lx",
				y: "ly"
			}
		};
		
		var lyrAlphaText = this.getLayer(constants.TMDS_ALPHATEXT);
		
		if (lyrAlphaText) {
			lyrAlphaText.loadData(data, metadata);
		}
		else {
			lyrAlphaText = new Layer(data, metadata, {
				renderer: renAlphaText.toJson()
			});
			
			this.addLayer(lyrAlphaText, {tmdsType: constants.TMDS_ALPHATEXT});			
		}
	}
	
	/**
	 * construct a schematic
	 * @param id {String} id of the schematic container
	 * @param options {Object} optional parameters with the support of following properties
	 * 		{
	 * 			env: "test" | dev" | "trial" | "prod", 				// environment of the data services; default to "prod"
     *          tmdsEnv: {String},                                  // TMDS environment name. This is for TEST env only, and will load data from dynamic cache
	 * 			useTrackAltName: <true | false>		                // true to switch the alternativeName with trackName; default to false.
	 * 		}
	 */
	var Schematic = function(id, options) {
		var idSelector = "#" + id;
		
		if ($(idSelector).length === 0) {
			throw new Error("The div element with id='" + id + "' does not exist.");
			return;
		}
		
		// if options is provided, merge them with the Schematic object.
		if (options) {
			$.extend(this, options);
		}
		
		// id: schematic id
		this.id = id;
		
		// jquery selection of the schematic
		// used to get node related data
		this._root = $(idSelector);
		
		// width, height: schematic dimension
		this.width = this._root.width();
		this.height = this._root.height();
		
		/**
		 * dataLayerIds: array of data layer ids
		 * @term a data layer is a layer of type {tmds.layers.Layer}, but not a tmds layer.
		 * The data of a data layer normally contains these key info:
		 * 	For points: trackName, milepost
		 * 	For lines: trackName, beginning milepost, end milepost
		 * which will be used to calculate the location or geometry of the data where to 
		 * render on the tmds schematic. Track data will be used as the reference data for 
		 * the calculation.
		 */
		this.dataLayerIds = [];
		
		// _dataLayers: array of data layers; mainly for internal use
		this._dataLayers = [];
		
		/**
		 * _tmdsLayers: {Object} key-value pair object. Each key is the tmds element type, and each value is its associated tmds layer.
		 * Each layer is of type {tmds.layers.Layer}
		 * 	{
		 * 		track: <trackLayer>,
		 * 		signal: <signalLayer>,
		 * 		switch: <switchLayer>,
		 * 		railCrossing: <railCrossingLayer>,
		 * 		controlPoint: <controlPointLayer>,
		 * 		alphaText: <alphaTextLayer>
		 * 	}
		 */
		this._tmdsLayers = {};
		
		// milepost range info object for the loaded subdivision 
		this.mpRangeInfo = null;
		
		// create the root container node for svg and other nodes, like zoom buttons
		var schmRoot = d3.select(idSelector)
			.append("div")
			.attr("id", "schematic_root")
			.attr("width", "100%")
			.attr("height", "100%");
		
		// define the zoom behavior
		this._zoomer = d3.behavior.zoom()
			// TODO: may need to include the scale extent in the constructor parameters
			.scaleExtent([ 0.25, 3 ])
			.on("zoom", onZoom.bind(this))
			.on("zoomstart", onZoomStart.bind(this))
			.on("zoomend", onZoomEnd.bind(this));
		
		// create svg container
		// _containerNode: d3 container node selection
		this._containerNode = schmRoot.append("svg")
				.attr("id", "schematicContainer")
				.attr("xmlns", "http://www.w3.org/2000/svg")
				.call(this._zoomer)
				.append("g");
		
		// add zoom buttons and define their behaviors
		var zoomContainer = schmRoot.append("div")
			.attr("id", "tmdsZoomContainer")
			.classed("clsTmdsZoomButton clsTmdsZoomButtonVertical", true);
		
		zoomContainer.append("div")
			.classed("clsTmdsZoomButtonIncrement", true)
			.html("+")
			.on("click", btnZoomIn_onClick.bind(this))
			.on("mousedown", doNothing)
			.on("mousemove", doNothing)
			.on("mouseup", doNothing);
	
		zoomContainer.append("div")
			.classed("clsTmdsZoomButtonDecrement", true)
			.html("-")
			.on("click", btnZoomOut_onClick.bind(this))
			.on("mousedown", doNothing)
			.on("mousemove", doNothing)
			.on("mouseup", doNothing);
				
		// initialize the TMDS layers
		for (var key in constants) {
			if (constants.hasOwnProperty(key)) {
				switch(constants[key]) {
					case constants.TMDS_TRACK: _loadTracks.call(this, []); break;
					case constants.TMDS_SWITCH: _loadSwitches.call(this, []); break;
					case constants.TMDS_SIGNAL: _loadSignals.call(this, []); break;
					case constants.TMDS_INTERLOCKER: _loadInterlockers.call(this, []); break;
					case constants.TMDS_CONTROLPOINT: _loadControlPoints.call(this, []); break;
					case constants.TMDS_ALPHATEXT: _loadAlphaTexts.call(this, []); break;
				}
			}			
		}
	};
	
	/**
	 * resize the schematic with the root element size changes
	 */
	function resize() {
		this.width = this._root.width();
		this.height = this._root.height();
	}
	
	/**
	 * add a layer to the schematic
	 * @param layer {tmds.layers.Layer}
	 * @param options {Object} option object with the following structure
	 * 	{
	 * 		tmdsType: "track" | "signal" | "switch" | "railCrossing" | "controlPoint" | "alphaText"		// required when layerType == "tmds"
	 * 	}
	 */
	function addLayer(layer, options) {
		if (options && options.tmdsType && validator.isValidValue(options.tmdsType, "TMDS", constants)) {
			layer.id = options.tmdsType;
			this._tmdsLayers[options.tmdsType] = layer;	
		}
		else {
			this._dataLayers.push(layer);
			this.dataLayerIds.push(layer.id);
		}
		
		// create the layer node to contain all the svg elements of the layer
		var lyrNodeSelection = this._containerNode.append("g").attr("id", layer.id);
		layer.setNode(lyrNodeSelection.node());
		
		// for each layer element, create a svg node
		$.each(layer.elements, function(_, element) {
			element.createNode();
		});
		
		layer.setSchematic(this);
		
		// set the visibility
		if (layer.visible) {
			layer.show();
		} else {
			layer.hide();
		}
		
		// set layer.loaded to true
		layer.loaded = true;
		
		// apply the transform to the layer				
		layer.redraw();
		
		// trigger Layer.onLoad()
		layer.trigger("load");
		
		return layer;
	}
	
	/**
	 * remove a data layer from the schematic
	 * @param layer {tmds.layers.Layer}
	 * @note cannot remove tmds layer
	 */
	function removeLayer(layer) {
		var lyrIdx = this.dataLayerIds.indexOf(layer.id);
		
		if (lyrIdx > -1) {
			this.dataLayerIds.splice(lyrIdx, 1);
			this._containerNode.node().removeChild(layer.getNode());				
		}
	}
	
	function _removeAllLayers() {
		var tmdsLayers = [];
		for (var lyrName in this._tmdsLayers) {
			if (this._tmdsLayers.hasOwnProperty(lyrName) && this._tmdsLayers[lyrName]) {
				tmdsLayers.push(this._tmdsLayers[lyrName]);
			}				
		}
		
		tmdsLayers.concat(this._dataLayers).forEach(function(lyr) {
			this.removeLayer(lyr);
		}, this);
	}
	
	/**
	 * redraw the whole schematic by applying d3 transformation to each loaded layer
	 */
	function redraw(){
		var zoomer = this._zoomer;
		
		var tmdsLayers = [];
		for (var lyrName in this._tmdsLayers) {
			if (this._tmdsLayers.hasOwnProperty(lyrName) && this._tmdsLayers[lyrName]) {
				tmdsLayers.push(this._tmdsLayers[lyrName]);
			}				
		}
		
		tmdsLayers.concat(this._dataLayers).forEach(function(lyr) {
			lyr.redraw();
		});
	}
	
	/**
	 * get a layer by its id
	 * @param id {String} layer id
	 * @return the layer
	 */
	function getLayer(id) {
		var layer = null,
			layerList = [];
		
		this._dataLayers.forEach(function(aLayer) { layerList.push(aLayer); });
		Object.keys(this._tmdsLayers).forEach(function(key) { this._tmdsLayers[key] && layerList.push(this._tmdsLayers[key]); }, this);
		
		layerList.forEach(function(aLayer) {
			if (aLayer.id === id) {
				layer = aLayer;
				return false;
			}
		});
		
		return layer;
	}
	
	/**
	 * get the current schematic scale level
	 */
	function getScale() {
		return this._zoomer.scale();
	}

	/**
	 * set the current schematic scale level
	 * @param s {Number} new scale level to set
	 * @return the schematic
	 */
	function setScale(s) {			
		var newScale = validateScale(s, this._zoomer.scaleExtent());
		
		if (newScale) {
			this._zoomer.scale(newScale);
			redraw.call(this);
		}
		
		return this;
	}
	
	/**
	 * set the schematic to wrap the extent
	 * @param ext {tmds.geometry.Extent}
	 * @return the schematic
	 * @note if ext is a point, center and zoom to that point
	 */
	function setExtent(ext) {
		var ew = ext.getWidth(),
			eh = ext.getHeight(),
			ec = ext.getCenter(),
			scaleX = ew > 0 ? this.width / ew : 10,
			scaleY = eh > 0 ? this.height / eh : 10,
			scale = ew > eh ? scaleX : scaleY;
			
		this.centerAndZoom(ec.x, ec.y, scale);
		
		return this;
	}
	
	/**
	 * set the cursor for the schematic
	 * @param cursor {String} A standard CSS cursor value
	 * @return the schematic
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/cursor for valid values
	 */
	function setCursor(cursor) {
		$("#" + this.id).css("cursor", cursor);
		
		return this;
	}
	
	/**
	 * load schematic data for the given subdivision
	 * @param subName {String} the subdivision name
	 * @return {Object} jQuery XMLHttpRequest (jqXHR) object
	 * @note when all TMDS data is loaded, it will trigger the "load" event
	 * @see http://api.jquery.com/jquery.ajax/
	 */
	function setSubdivision(subName) {
		var me = this;
		
		this.setCursor("wait");
		
		// clear all TMDS layers and mpRangeInfo
		for (var lyrId in this._tmdsLayers) {
			if (this._tmdsLayers.hasOwnProperty(lyrId) && this._tmdsLayers[lyrId] instanceof Layer) {
				this._tmdsLayers[lyrId].clear();
			}				
		}
		
		this.mpRangeInfo = null;
		
		// cancel the previous requests if they are not completed when new requests come in.
		if (defMaster && defMaster.state() !== "resolved") {
			[defTMDS, defMPRange].forEach(function(def) {
				def && def.readyState !== 4 && def.abort();
			});
		}
		
		// master deferred object to handle both schematic and mpRange service call
		defMaster = $.Deferred().always(function() {
			me.setCursor("default");
		});
		
        // counter of resolved service calls
		var cntResolve = 0;
		
		// function to call when one service call is resolved
		var funResolve = function() {
			cntResolve++;
			
			if (cntResolve === (mpRangeGraphUrl ? 3 : 2)) {
				// trigger "load" event
				trigger.call(me, "load");
			}
		};
        
		var tmdsUrl = config.tmdsUrl(subName, this.env, this.tmdsEnv),
            mpRangeUrl = config.mpRangeUrl(subName, this.env, this.tmdsEnv),
            mpRangeGraphUrl = config.mpRangeGraphUrl(subName, this.env, this.tmdsEnv);

        //
		// load TMDS schematic data for the given subdivision
        //
		defTMDS = $.ajax({
			url: tmdsUrl, 
			dataType: "json",
			timeout: 30000		// timeout in 30 seconds
		}).done(function(response) {
			var tmds = response;
			
			// reject defMaster when schematic service call returns null or {}
			if (!tmds || $.isEmptyObject(tmds)) {
				defMaster.reject("No schematic data is returned for " + subName);
			}
			
			// create each layer using layerData
			for (var key in tmds) {
				switch(key) {
					case constants.TMDS_TRACK: _loadTracks.call(me, tmds[key]); break;
					case constants.TMDS_SWITCH: _loadSwitches.call(me, tmds[key]); break;
					case constants.TMDS_SIGNAL: _loadSignals.call(me, tmds[key]); break;
					case constants.TMDS_INTERLOCKER: _loadInterlockers.call(me, tmds[key]); break;
					case constants.TMDS_CONTROLPOINT: _loadControlPoints.call(me, tmds[key]); break;
					case constants.TMDS_ALPHATEXT: _loadAlphaTexts.call(me, tmds[key]); break;
				}
			}
			
            defMaster.resolve();
            
		})
		.fail(function(jqXHR, textStatus, err) {
			defMaster.reject("Failed to load schematic data for " + subName + " subdivision");
		})
        .always(function() {
            funResolve();
        });

        //
        // load milepost range info from mainframe for the given subdivision
        //
		defMPRange = $.ajax({
			url: mpRangeUrl, 
			dataType: "json",
			timeout: 30000		// timeout in 30 seconds
		}).done(function(response) {
			var mpRangeInfo = response;
			
			// reject defMaster when mpRange service call returns null or {}
			if (!mpRangeInfo || $.isEmptyObject(mpRangeInfo)) {
				console.error("No milepost range info is returned for " + subName);
                return;
			}

            // format and save the milepost range info
            me.mpRangeInfo = formatMpRangeInfo(mpRangeInfo);
            
		})
		.fail(function(jqXHR, textStatus, err) {
			console.error("Failed to load milepost range info for " + subName + " subdivision");            
		}).always(function() {
            funResolve();
        });

        //
        // load milepost range graph for the given subdivision
        //
        if (mpRangeGraphUrl) {
            defMPRangeGraph = $.ajax({
                url: mpRangeGraphUrl, 
                dataType: "json",
                timeout: 30000		// timeout in 30 seconds
            }).done(function(response) {
                var mpRangeGraph = response;

                // reject defMaster when mpRange service call returns null or {}
                if (!mpRangeGraph || $.isEmptyObject(mpRangeGraph)) {
                    console.error("No milepost range graph is returned for " + subName);
                    return;
                }

                // format and save the milepost range info
                me.mpRangeGraph = mpRangeGraph;

            })
            .fail(function(jqXHR, textStatus, err) {
                console.error("Failed to load milepost range graph for " + subName + " subdivision");
            }).always(function() {
                funResolve();
            });
        }
	
		return defMaster.promise();
	}
    
    /**
     * set schematic.useTrackAltName property with the given boolean value
     * @param b {Boolean}
     * @note The change of schematic.useTrackAltName will apply to the loaded track layer. 
     * If lyrTrack["useTrackAltName"] <> b, switch the track name with alternate name for 
     * the loaded track layer. If lyrTrack["useTrackAltName"] == b, do not switch.
     */
    function setUseTrackAltName(b) {
        var lyrTrack = this.getLayer(constants.TMDS_TRACK);
        
        if (lyrTrack && lyrTrack["useTrackAltName"] !== b) {
            var tracks = lyrTrack.elements.map(function(aElem) {
                return aElem.attributes;
            });
            
            _switchTrackName(tracks);
            lyrTrack["useTrackAltName"] = b;
        }
        
        this.useTrackAltName = b;
    }
	
	/**
	 * move the given location to the center of the schematic
	 * @param x {Number} x coordinate of the new center location
	 * @param y {Number} y coordinate of the new center location
	 * @return the schematic
	 * @note (x, y) are the values without any transformation applied.
	 */
	function panTo(x, y) {
		// calculate the actual location of (x,y) on the screen based on the current transformation
		var screenPoint = toScreenPoint.call(this, x, y);
		
		// remember the current translate
		var cntTranslate = this._zoomer.translate();
		
		// combine the current translate with the offset needed to move the point to center
		this._zoomer.translate([cntTranslate[0] + this.width / 2 - screenPoint.x, cntTranslate[1] + this.height / 2 - screenPoint.y]);
		
		// update the schematic
		redraw.call(this);
		
		return this;
	}
	
	/**
	 * move the schematic center to given location, and zoom to the given scale
	 * @param x {Number} x coordinate of the new center location
	 * @param y {Number} y coordinate of the new center location
	 * @param scale {Number} the new scale level
	 * @return the schematic
	 */
	function centerAndZoom(x, y, scale) {
		// 1. apply the new scale
		var newScale = validateScale(scale, this._zoomer.scaleExtent());
		
		if (newScale) {
			setScale.call(this, newScale);
		}

		// 2. center at the new location
		if (typeof x === "number" && typeof y === "number") {
			panTo.call(this, x, y);	
		}
		
		return this;
	}
	
	/**
	 * disable schematic navigation
	 * @return the schematic
	 * @note schematic navigation should be disabled when other tools are interacting with the schematic, like drawing tool
	 */
	function disableNavigation() {
		d3.select("#schematicContainer").call(this._zoomer)
		    .on("mousedown.zoom", null)
		    .on("mousemove.zoom", null)
		    .on("touchstart.zoom", null)
		    .on("touchmove.zoom", null)
		    .on("touchend.zoom", null);
		return this;
	}
	
	/**
	 * enable schematic navigation
	 * @return the schematic
	 */
	function enableNavigation() {
		d3.select("#schematicContainer").call(this._zoomer);
		return this;
	}
	
	/**
	 * event handler when click the zoom-in button
	 * zoom in the schematic one level down
	 */
	function btnZoomIn_onClick() {
		setScale.call(this, getScale.call(this) * (1 + 0.1));
		this._zoomer.event(this._containerNode);
	}
	
	/**
	 * event handler when click the zoom-out button
	 * zoom out the schematic one level up
	 */
	function btnZoomOut_onClick(e) {
		setScale.call(this, getScale.call(this) * (1 - 0.1));
		this._zoomer.event(this._containerNode);
	}
	
	/**
	 * convert screen point to geometry point without transformation applied
	 * @param x {Number} pageX
	 * @param y {Number} pageY
	 * @return {Object} the geometry point {x, y}
	 */
	function toGeomPoint(x, y) {
		var scale = this._zoomer.scale(), translate = this._zoomer.translate();

		// convert page (x,y) to (x,y) that is related to the schematic container
		x = x - this._root.offset().left,
		y = y - this._root.offset().top;
		
		return {
			x: (x - translate[0]) / scale, 
			y: (y - translate[1]) / scale
		};
	}
	
	/**
	 * convert geometry point to screen point with transformation applied
	 * @param x {Number} x coordinate in geometry unit
	 * @param y {Number} y coordinate in geometry unit
	 * @return {Object} the screen point {x, y} related to the schematic container node
	 */
	function toScreenPoint(x, y) {
		var scale = this._zoomer.scale(), translate = this._zoomer.translate();
		return {
			x: x * scale + translate[0], 
			y: y * scale + translate[1]
		};
	}

	/**
	 * convert track name and milepost to points in a subdivision
	 * @param subdiv {String} subdivision name
	 * @param trkName {String} track name, like M1, M2, Siding, etc
	 * @param mp {Number} milepost.
	 * @param mpSuf {String} milepost suffix.
	 * @return {Array} of {tmds.geometry.Point} list of matching points
	 */
	function toPoints(subdiv, trkName, mp, mpSuf) {
		var points = [], matchingTracks;

		var lyrTrack = this._tmdsLayers[constants.TMDS_TRACK];
		if (lyrTrack && lyrTrack.elements.length > 0) {
			// filter the tracks that will match subdivision, track name and mp range
			matchingTracks = findHostTracks(lyrTrack, subdiv, trkName, mp, mpSuf);
			
			// interpolate the point
			matchingTracks.forEach(function(aTrack) {
				points.push(trackInterpolate(aTrack, mp));
			});
		}
		
		return points;
	}
	
	/**
	 * convert track name and milepost range to a polyline
	 * @param subdiv {String} subdivision short name
	 * @param trkName {String} track name, like M1, M2, Siding, etc
	 * @param bmp {Number} beginning milepost
	 * @param emp {Number} end milepost
	 * @param bmpSuf {String} optional. beginning milepost suffix
	 * @param empSuf {String} optional. end milepost suffix
	 * @return {tmds.geometry.Polyline}
	 * @note The algorithm assumes that with the combination of subdiv, track name, and mp range, it exists only one 
	 * result polyline. Otherwise, the algorithm won't work.
	 */
	function toPolyline(subdiv, trkName, bmp, emp, bmpSuf, empSuf) {
		var polyline = null;
		var bTracks, bTrack, eTracks, eTrack, bRange, eRange;
		var	lTrack, rTrack, lmp, rmp;
		var paths = [], path = [], bngPoint, endPoint, innerTracks;
		
		// get the track layer reference
		var lyrTrack = this._tmdsLayers[constants.TMDS_TRACK];
		
		if (lyrTrack && lyrTrack.elements.length > 0) {
			// find the track to host bmp
			bTracks = findHostTracks(lyrTrack, subdiv, trkName, bmp, bmpSuf);
			bTrack = bTracks.length > 0 ? bTracks[0] : null;

			// find the track to host emp
			eTracks = findHostTracks(lyrTrack, subdiv, trkName, emp, empSuf);
			eTrack = eTracks.length > 0 ? eTracks[0] : null;
			
			if (bTrack && eTrack) {
				// get the milepost range for bmp and emp
				bRange = getRangeByTrack.call(this, bTrack);
				eRange = getRangeByTrack.call(this, eTrack);

                if (!bRange || !eRange) {
                    return;
                }
                
				//
				// calculate the milepost, host track for the left point
				// calculate the milepost, host track for the right point
				//
				if (bRange.sequence === eRange.sequence) {
					lmp = Math.abs(bmp - bRange.from) < Math.abs(emp - bRange.from) ? bmp : emp;
					rmp = Math.abs(bmp - bRange.from) < Math.abs(emp - bRange.from) ? emp : bmp;
				}
				else {
					lmp = (bRange.sequence < eRange.sequence) ? bmp : emp;				// milepost of the left point
					rmp = (bRange.sequence < eRange.sequence) ? emp : bmp;				// milepost of the right point
				}

				lTrack = lmp === bmp ? bTrack : eTrack;									// track to host the left point
				rTrack = lmp === bmp ? eTrack : bTrack;									// track to host the right point
				
				// calculate the beginning and end point of the result
				bngPoint = trackInterpolate(lTrack, lmp);								// calculate the left point
				endPoint = trackInterpolate(rTrack, rmp);								// calculate the right point

				// get the internal tracks that sit in bwteen bmp and emp with matching subdiv and trkName
				innerTracks = findInnerTracks.call(this, lyrTrack, subdiv, trkName, bmp, emp, bmpSuf, empSuf);

				// if lTrack <> rTrack,
				if (lTrack.attributes[trackFields.TRACK_LX] !== rTrack.attributes[trackFields.TRACK_LX] || 
						lTrack.attributes[trackFields.TRACK_RX] !== rTrack.attributes[trackFields.TRACK_RX] ||
						lTrack.attributes[trackFields.TRACK_LY] !== rTrack.attributes[trackFields.TRACK_LY] ||
						lTrack.attributes[trackFields.TRACK_RY] !== rTrack.attributes[trackFields.TRACK_RY]) {
					// add the first path
					path.push([bngPoint.x, bngPoint.y]);					
					path.push([lTrack.geometry["x2"], lTrack.geometry["y2"]]);
					paths.push(path);
					
					// if the result across multiple tracks,
					if (innerTracks.length > 0) {
						// add internal tracks
						innerTracks.sort(function(a, b) { return (a.geometry.y1 - b.geometry.y1) * 10000 + (a.geometry.x1 - b.geometry.x1); });
						
						innerTracks.forEach(function(aTrack) {
							var geom = aTrack.geometry;
							paths.push([[geom.x1, geom.y1], [geom.x2, geom.y2]]);
						});
					}
						
					// add the last path
					path = [];
					path.push([rTrack.geometry["x1"], rTrack.geometry["y1"]]);
					path.push([endPoint.x, endPoint.y]);					
					paths.push(path);
				}
				// if lTrack = rTrack,
				else {
					// bngPoing and endPoint sit on the same track
					path.push([bngPoint.x, bngPoint.y]);
					path.push([endPoint.x, endPoint.y]);
					
					paths.push(path);
				}
				
				polyline = new Polyline({paths: paths});
			}
		}
		
		return polyline;
	}
	
	/**
	 * Events
	 */

	/**
	 * helper function to wrap jQury trigger to trigger events
	 */
	function trigger(evtType, params) {
		$(this).trigger(evtType, params);
	}
	
	/**
	 * enable to define event handler of the schematic
	 * @param evtType {String} event type
	 * @param callback {Function} callback function
	 */
	function on(evtType, callback) {
		switch (evtType) {
			case "load":
				$(this).on("load", callback);
				break;
			case "zoom":
			case "zoom-start":
			case "zoom-end":
				onNavigate.call(this, evtType, callback);
				break;
			default:
				this._root.on(evtType, callback);
				break;
		}
		
		return this;
	}
	
	/**
	 * detach an event handler
	 * @param evtType {String} event type
	 * @param callback {Function} event handler to be detached
	 */
	function off(evtType, callback) {
		this._root.off(evtType, callback);
		
		return this;
	}
	
	/**
	 * event handler when zooming the schematic
	 */
	function onZoom(e) {
		redraw.call(this);
		trigger.call(this, "zoom", d3.event);
	}
	
	/**
	 * event handler when start zooming the schematic
	 */
	function onZoomStart(e) {
		trigger.call(this, "zoom-start", d3.event);
	}
	
	/**
	 * event handler when zooming the schematic ends
	 */
	function onZoomEnd(e) {
		trigger.call(this, "zoom-end", d3.event);
	}
	
	/**
	 * event handler for navigation operations
	 * @param evtType {String} event type
	 * @param callback {Function} callback function to respond to the event
	 */
	function onNavigate(evtType, callback) {
		// reorganize the event object
		// d3Evt is the d3.js event object passed by d3.behavior.zoom
		$(this).on(evtType, function(e, d3Evt) {
			return callback($.extend(d3Evt, {
				target: e.target,
				type: e.type,
				timeStamp: e.timeStamp
			}));
		});
	}
	
	/**
	 * define static methods
	 */
	Schematic.tmdsLayerIds = function() { return constants; };
	Schematic.trackKeyFields = function() { return trackFields; };
	
	/**
	 * define instance properties and methods
	 */
	Schematic.prototype = {
		declaredClass: "tmds.Schematic",
		'addLayer' : addLayer,
		'removeLayer' : removeLayer,
		'redraw' : redraw,
		'resize' : resize,
		'getLayer' : getLayer,
		'getScale' : getScale,
		'setScale' : setScale,
		'setExtent' : setExtent,
		'setCursor' : setCursor,
		'setSubdivision' : setSubdivision,
        'setUseTrackAltName' : setUseTrackAltName,
		'panTo' : panTo,
		'centerAndZoom' : centerAndZoom,
		'disableNavigation' : disableNavigation,
		'enableNavigation' : enableNavigation,
		'toGeomPoint' : toGeomPoint,
		'toScreenPoint' : toScreenPoint,
		'toPoints' : toPoints,
		'toPolyline' : toPolyline,
		'on' : on,
		'off' : off
	};
	
	return Schematic;
});